import React, { useEffect } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectOrganizationsData } from '../../store/organization/organization-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import CustomAlert from '@common/components/Feedback/CustomAlert';

import { MoveRightArrow } from '@common/utils/icons/icons';
import { replaceArgsInMessage } from '../../utils/strings';
import { resetOrganizationsRestState } from '../../store/organization/organization-slice';
import {
  deleteOrganizationById,
  getOrganizationDeleteDataById,
} from '../../store/organization/organization-actions';
import DeleteAccordionList from '../misc/DeleteAccordionList';
import OrganizationRole from '../../models/enums/organization-roles';
import ListOrganization from '../../models/organization/list-organization';

const DeleteDialog: React.FC<{
  organization?: ListOrganization;
  open: boolean;
  onClose: () => void;
}> = ({ organization, open, onClose }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const organizationData = useAppSelector((state) => selectOrganizationsData(state));

  useEffect(() => {
    if (open) {
      dispatch(
        resetOrganizationsRestState({
          getDeleteStatus: HttpFetchStatus.Idle,
          getDeleteError: null,
        }),
      );
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (
      organizationData.rest.getDeleteStatus === HttpFetchStatus.Idle &&
      organization?.id != null
    ) {
      dispatch(getOrganizationDeleteDataById(organization.id));
    }
  }, [dispatch, organization?.id, organizationData.rest.getDeleteStatus]);

  if (organization == null) return null;

  const closeHandler = () => {
    onClose();
    dispatch(
      resetOrganizationsRestState({
        deleteStatus: HttpFetchStatus.Idle,
        deleteError: null,
      }),
    );
  };

  const deleteDisabled =
    organization.role === OrganizationRole.MANUFACTURER ||
    organizationData.rest.deleteStatus === HttpFetchStatus.Loading ||
    organizationData.rest.getDeleteStatus !== HttpFetchStatus.Success;

  const deleteOrganizationClickHandler = () => {
    dispatch(deleteOrganizationById(organization.id));
  };

  return (
    <React.Fragment>
      <Dialog open={open} onClose={closeHandler} fullWidth maxWidth="sm">
        <DialogTitle>{`${t('title-delete')} ${organization.name}`}</DialogTitle>
        <DialogContent>
          <CustomAlert
            severity="warning"
            header={t('warning-deleting-is-permanent')}
            sx={{ mb: 2 }}
          />
          <DeleteAccordionList
            title={
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography>
                  {replaceArgsInMessage(t('info-moved-robots'), [
                    organizationData.deleteData?.robots.length ?? '-',
                  ])}
                </Typography>
                <MoveRightArrow fontSize="large" />
                <Typography>{organization.parentName}</Typography>
              </Stack>
            }
            loadingText={t('loading-robots')}
            isLoading={organizationData.rest.getDeleteStatus === HttpFetchStatus.Loading}
            items={(organizationData.deleteData?.robots ?? []).map((robot, index) => ({
              id: index.toString(),
              name: robot,
            }))}
          />
          <DeleteAccordionList
            title={
              <Typography>
                {replaceArgsInMessage(t('info-deleted-users'), [
                  organizationData.deleteData?.users.length ?? '-',
                ])}
              </Typography>
            }
            loadingText={t('loading-users')}
            isLoading={organizationData.rest.getDeleteStatus === HttpFetchStatus.Loading}
            items={(organizationData.deleteData?.users ?? []).map((user, index) => ({
              id: index.toString(),
              name: user,
            }))}
          />
          {organization.role === OrganizationRole.IMPORTER && (
            <DeleteAccordionList
              title={
                <Typography>
                  {replaceArgsInMessage(t('info-deleted-wholesalers'), [
                    organizationData.deleteData?.wholesalers?.length ?? '-',
                  ])}
                </Typography>
              }
              loadingText={t('loading-wholesalers')}
              isLoading={organizationData.rest.getDeleteStatus === HttpFetchStatus.Loading}
              items={(organizationData.deleteData?.wholesalers ?? []).map((org, index) => ({
                id: index.toString(),
                name: org,
              }))}
            />
          )}
          {[OrganizationRole.IMPORTER, OrganizationRole.WHOLESALER].includes(organization.role) && (
            <DeleteAccordionList
              title={
                <Typography>
                  {replaceArgsInMessage(t('info-deleted-distributors'), [
                    organizationData.deleteData?.distributors?.length ?? '-',
                  ])}
                </Typography>
              }
              loadingText={t('loading-distributors')}
              isLoading={organizationData.rest.getDeleteStatus === HttpFetchStatus.Loading}
              items={(organizationData.deleteData?.distributors ?? []).map((org, index) => ({
                id: index.toString(),
                name: org,
              }))}
            />
          )}
          {[
            OrganizationRole.IMPORTER,
            OrganizationRole.WHOLESALER,
            OrganizationRole.DISTRIBUTOR,
          ].includes(organization.role) && (
            <DeleteAccordionList
              title={
                <Typography>
                  {replaceArgsInMessage(t('info-deleted-customers'), [
                    organizationData.deleteData?.customers.length ?? '-',
                  ])}
                </Typography>
              }
              loadingText={t('loading-customers')}
              isLoading={organizationData.rest.getDeleteStatus === HttpFetchStatus.Loading}
              items={(organizationData.deleteData?.customers ?? []).map((org, index) => ({
                id: index.toString(),
                name: org,
              }))}
            />
          )}
          <DeleteAccordionList
            title={
              <Typography>
                {replaceArgsInMessage(t('info-deleted-locations'), [
                  organizationData.deleteData?.locations.length ?? '-',
                ])}
              </Typography>
            }
            loadingText={t('loading-locations')}
            isLoading={organizationData.rest.getDeleteStatus === HttpFetchStatus.Loading}
            items={(organizationData.deleteData?.locations ?? []).map((org, index) => ({
              id: index.toString(),
              name: org,
            }))}
          />
        </DialogContent>
        <DialogActions>
          <Stack direction="row" spacing={1}>
            <Button onClick={closeHandler}>{t('button-cancel')}</Button>
            <Button
              color="error"
              data-testid="button-delete"
              variant="contained"
              disabled={deleteDisabled}
              onClick={deleteOrganizationClickHandler}
            >
              {organizationData.rest.deleteStatus === HttpFetchStatus.Loading ? (
                <CircularProgress size={24} />
              ) : (
                t('button-delete')
              )}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default DeleteDialog;
